import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { invokeFunction } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { FileText, Loader2 } from "lucide-react";

interface InsightReportViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  courseTitle?: string;
}

export default function InsightReportView({
  open,
  onOpenChange,
  courseId,
  courseTitle = "Course",
}: InsightReportViewProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    setContent(null);
    setSummary(null);

    const { data, error: err } = await invokeFunction("generate-insight-report", {
      body: { courseId, reportType: "on_demand" },
    });

    setLoading(false);
    if (err) {
      setError(err.message || "Failed to generate report");
      return;
    }
    if (data?.content) {
      setContent(data.content);
      setSummary(data.summary ?? null);
    } else {
      setError("No report content returned");
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setContent(null);
    setSummary(null);
    setError(null);
  };

  const handleLinkClick = (href: string) => {
    if (href?.startsWith("/learn/")) {
      handleClose();
      navigate(href);
    }
  };

  const markdownComponents = {
    a: ({ href, children, ...props }: { href?: string; children?: React.ReactNode }) => {
      if (href?.startsWith("/learn/")) {
        return (
          <button
            type="button"
            className="text-primary underline hover:no-underline"
            onClick={() => handleLinkClick(href)}
          >
            {children}
          </button>
        );
      }
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      );
    },
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? handleClose() : onOpenChange(o))}>
      <DialogContent className="max-w-2xl max-h-[90dvh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Learning Insight Report
          </DialogTitle>
          <DialogDescription>
            AI-generated report for {courseTitle}
          </DialogDescription>
        </DialogHeader>

        {!content && !loading && (
          <div className="py-8 text-center space-y-4">
            <p className="text-sm text-muted-foreground">
              Get a comprehensive report on your progress, strengths, areas to focus on, and recommended next steps.
            </p>
            <Button onClick={handleGenerate} className="gap-2">
              <FileText className="h-4 w-4" />
              Generate Report
            </Button>
          </div>
        )}

        {loading && (
          <div className="py-12 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Generating your report...</p>
          </div>
        )}

        {error && (
          <div className="py-4 px-4 rounded-lg bg-destructive/10 text-destructive text-sm">
            {error}
          </div>
        )}

        {content && !loading && (
          <ScrollArea className="flex-1 pr-4 -mr-4" style={{ maxHeight: "calc(90dvh - 120px)" }}>
            {summary && (
              <div className="mb-4 p-3 rounded-lg bg-muted/60 text-sm">
                <p className="font-medium text-muted-foreground mb-1">Summary</p>
                <p>{summary}</p>
              </div>
            )}
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
                {content}
              </ReactMarkdown>
            </div>
          </ScrollArea>
        )}

        {content && !loading && (
          <Button variant="outline" onClick={handleGenerate} className="mt-4 gap-2" disabled={loading}>
            <FileText className="h-4 w-4" />
            Generate New Report
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
